import React, { useState, useEffect, useRef } from 'react';
import { Brain, X, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

type Message = {
  id: string;
  content: string;
  sender: 'user' | 'bot';
  timestamp: Date;
};

const translations = {
  es: {
    title: 'Asistente CortexAI',
    placeholder: 'Escribe tu pregunta...',
    helpButton: '¿Necesitas ayuda?',
    welcome: '¡Hola! Soy el asistente de CortexAI. ¿En qué puedo ayudarte? Puedes preguntarme sobre:\n\n1. Nuestros programas de formación\n2. Roles y habilidades disponibles\n3. Proceso de aprendizaje\n4. Precios y financiación\n5. Experiencia virtual de empleo (DCVEE)',
    fallback: 'No estoy seguro de cómo ayudarte con eso. ¿Podrías reformular tu pregunta? Puedo informarte sobre:\n\n1. Programas de formación\n2. Roles disponibles\n3. Proceso de aprendizaje\n4. Precios\n5. Experiencia virtual de empleo (DCVEE)'
  },
  en: {
    title: 'CortexAI Assistant',
    placeholder: 'Type your question...',
    helpButton: 'Need help?',
    welcome: 'Hi! I\'m the CortexAI assistant. How can I help you? You can ask me about:\n\n1. Our training programs\n2. Available roles and skills\n3. Learning process\n4. Pricing and financing\n5. Digital Company & Virtual Employment Experience (DCVEE)',
    fallback: 'I\'m not sure how to help with that. Could you rephrase your question? I can tell you about:\n\n1. Training programs\n2. Available roles\n3. Learning process\n4. Pricing\n5. Digital Company & Virtual Employment Experience (DCVEE)'
  }
};

const responses = {
  es: {
    pricing: 'Ofrecemos planes flexibles de pago y financiación a través de Klarna. Puedes pagar en cuotas mensuales para hacer la formación más accesible. También tenemos un plan gratuito para que puedas empezar a explorar la plataforma.',
    roles: 'Formamos profesionales para roles tecnológicos como:\n\n- Administrador de Azure\n- Arquitecto de Azure\n- Especialista en Ciberseguridad de Azure\n- Ingeniero de IA de Azure\n\nCada rol incluye formación completa en todas las habilidades necesarias.',
    skills: 'Desarrollarás habilidades técnicas como:\n\n- Python\n- React\n- C#\n- Gestión de Bases de Datos\n- Desarrollo de APIs\n- ITIL\n\nY muchas más, adaptadas específicamente a tu rol elegido.',
    dcvee: 'La Experiencia Virtual de Empleo en Empresa Digital (DCVEE) te permite:\n\n- Experimentar un onboarding realista\n- Usar herramientas empresariales reales\n- Colaborar con equipos virtuales\n- Resolver desafíos reales\n- Recibir feedback continuo\n\nEs como trabajar en una empresa real antes de conseguir tu primer empleo.',
    certification: 'Ofrecemos certificaciones verificadas por IA y respaldadas por blockchain. Estas certificaciones demuestran tus habilidades prácticas y son reconocidas por empleadores. Las certificaciones son opcionales pero recomendadas para impulsar tu credibilidad profesional.',
    mentor: 'Tendrás acceso 24/7 a un mentor IA personal que:\n\n- Te guía en tu aprendizaje\n- Proporciona feedback detallado\n- Responde tus dudas\n- Adapta el contenido a tu ritmo\n- Te ayuda con desafíos técnicos'
  },
  en: {
    pricing: 'We offer flexible payment plans and financing through Klarna. You can pay in monthly installments to make training more accessible. We also have a free plan so you can start exploring the platform.',
    roles: 'We train professionals for technology roles such as:\n\n- Azure Administrator\n- Azure Architect\n- Azure Cybersecurity Specialist\n- Azure AI Engineer\n\nEach role includes complete training in all necessary skills.',
    skills: 'You\'ll develop technical skills like:\n\n- Python\n- React\n- C#\n- Database Management\n- API Development\n- ITIL\n\nAnd many more, specifically tailored to your chosen role.',
    dcvee: 'The Digital Company & Virtual Employment Experience (DCVEE) allows you to:\n\n- Experience realistic onboarding\n- Use real business tools\n- Collaborate with virtual teams\n- Solve real challenges\n- Receive continuous feedback\n\nIt\'s like working at a real company before getting your first job.',
    certification: 'We offer AI-verified and blockchain-backed certifications. These certifications demonstrate your practical skills and are recognized by employers. Certifications are optional but recommended to boost your professional credibility.',
    mentor: 'You\'ll have 24/7 access to a personal AI mentor who:\n\n- Guides your learning\n- Provides detailed feedback\n- Answers your questions\n- Adapts content to your pace\n- Helps with technical challenges'
  }
};

const FloatingChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isMinimized, setIsMinimized] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const { language } = useLanguage();
  const t = translations[language];
  const r = responses[language];

  useEffect(() => {
    // Add initial welcome message
    setMessages([
      {
        id: '1',
        content: t.welcome,
        sender: 'bot',
        timestamp: new Date()
      }
    ]);
  }, [language]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      content: input,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');

    // Generate bot response based on user input
    setTimeout(() => {
      const botResponse = generateResponse(input.toLowerCase());
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: botResponse,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    }, 1000);
  };

  const generateResponse = (input: string): string => {
    if (input.includes('precio') || input.includes('costo') || input.includes('pago') ||
        input.includes('price') || input.includes('cost') || input.includes('payment')) {
      return r.pricing;
    }

    if (input.includes('roles') || input.includes('trabajos') || input.includes('carreras') ||
        input.includes('jobs') || input.includes('careers')) {
      return r.roles;
    }

    if (input.includes('habilidades') || input.includes('tecnologías') ||
        input.includes('skills') || input.includes('technologies')) {
      return r.skills;
    }

    if (input.includes('dcvee') || input.includes('experiencia') || input.includes('virtual') ||
        input.includes('experience')) {
      return r.dcvee;
    }

    if (input.includes('certificación') || input.includes('certificado') ||
        input.includes('certification') || input.includes('certificate')) {
      return r.certification;
    }

    if (input.includes('mentor') || input.includes('ayuda') || input.includes('soporte') ||
        input.includes('help') || input.includes('support')) {
      return r.mentor;
    }

    return t.fallback;
  };

  return (
    <div className="fixed bottom-8 left-8 z-50">
      {isMinimized ? (
        <button
          onClick={() => setIsMinimized(false)}
          className="flex items-center space-x-2 bg-accent text-white px-4 py-2 rounded-full shadow-lg hover:bg-accent/90 transition-all duration-300"
        >
          <Brain className="h-6 w-6" />
          <span>{t.helpButton}</span>
          <ChevronUp className="h-4 w-4" />
        </button>
      ) : (
        <div
          ref={chatRef}
          className="bg-white rounded-lg shadow-xl w-96 transition-all duration-300"
        >
          {/* Header */}
          <div className="bg-accent text-white p-4 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center">
              <Brain className="h-6 w-6 mr-2" />
              <h3 className="font-medium">{t.title}</h3>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsMinimized(true)}
                className="p-1 hover:bg-white/20 rounded-full"
              >
                <ChevronDown className="h-5 w-5" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/20 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="h-96 overflow-y-auto p-4 bg-gray-50">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`mb-4 flex ${
                  message.sender === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.sender === 'user'
                      ? 'bg-accent text-purple-100'
                      : 'bg-white border border-gray-200'
                  }`}
                >
                  <p className={`text-sm whitespace-pre-line ${
                    message.sender === 'bot' ? 'text-purple-600' : ''
                  }`}>
                    {message.content}
                  </p>
                  <p className={`text-xs mt-1 ${
                    message.sender === 'user' ? 'text-purple-100' : 'text-purple-400'
                  }`}>
                    {message.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-4 border-t flex">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t.placeholder}
              className="flex-1 border border-gray-300 rounded-l-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent text-gray-900 placeholder-gray-500"
            />
            <button
              type="submit"
              className="bg-accent text-white px-4 py-2 rounded-r-lg hover:bg-accent/90 transition-colors"
            >
              <Send className="h-5 w-5" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default FloatingChatbot;